/**
 * WhatsApi Tenant Dashboard Tab - TASK-2291.
 *
 * Exibido como aba no Dashboard principal (Manager + Dev) — restrito ao
 * tenant selecionado. Reusa componentes do TASK-2258 via prop escopo (tenant).
 * Se o tenant não tem whatsapi_keys ativas, backend retorna 424 no_api_keys
 * e esta tab renderiza empty state com CTA para /whatsapi/api-keys.
 *
 * Nota: HealthStatusBadge já é renderizado internamente pelo StatCardsRow
 * (dentro do card de Status), portanto não é duplicado aqui.
 */
import { useMemo } from 'react'
import { Stack, SimpleGrid } from '@mantine/core'
import { IconKey } from '@tabler/icons-react'

import { EmptyState } from '@/components/ui'
import { StatCardsRow } from '@/components/whatsapi/StatCardsRow'
import { DeliveryFunnel } from '@/components/whatsapi/DeliveryFunnel'
import { ThroughputChart } from '@/components/whatsapi/ThroughputChart'
import { LatencyChart } from '@/components/whatsapi/LatencyChart'
import { ApiKeyTable } from '@/components/whatsapi/ApiKeyTable'
import { ErrorsTable } from '@/components/whatsapi/ErrorsTable'
import { WhatsApiPeriodProvider } from '@/context/WhatsApiPeriodContext'
import { isNoApiKeys, useOverview } from '@/hooks/useWhatsApiStats'
import type { EscopoWhatsApi } from '@/hooks/useEscopoWhatsApi'

export interface WhatsApiTenantTabProps {
  tenantId: number
}

export function WhatsApiTenantTab({ tenantId }: WhatsApiTenantTabProps) {
  const escopo = useMemo<EscopoWhatsApi>(() => ({ tipo: 'tenant', tenantId }), [tenantId])
  // Sonda um endpoint só (overview) para detectar 424 no_api_keys.
  // Os demais componentes também usariam useQuery com mesmo tenantId —
  // mas se já sabemos que não há keys, evita múltiplos requests simultâneos 424.
  const probe = useOverview('24h', { escopo })

  if (isNoApiKeys(probe.data)) {
    return (
      <EmptyState
        icon={IconKey}
        accent="info"
        title="Este tenant ainda não tem API keys do WhatsApi"
        description="Configure uma chave de API para começar a monitorar throughput, latência e erros de mensageria."
        primaryAction={{
          label: 'Ver API Keys',
          href: '/whatsapi/api-keys',
        }}
      />
    )
  }

  return (
    <WhatsApiPeriodProvider>
      <Stack data-testid="whatsapi-tenant-dashboard-root">
        <StatCardsRow escopo={escopo} />
        <DeliveryFunnel escopo={escopo} />
        <SimpleGrid cols={{ base: 1, md: 2 }}>
          <ThroughputChart escopo={escopo} />
          <LatencyChart escopo={escopo} />
        </SimpleGrid>
        <ApiKeyTable escopo={escopo} />
        <ErrorsTable escopo={escopo} />
      </Stack>
    </WhatsApiPeriodProvider>
  )
}
